import React from "react";
import { formatMoney, formatReach } from "../../inventory/parse";
import { formatDateTime, timeAgo } from "../../inventory/time";
import Icon from "../../components/ui/Icon";
import ColumnPicker from "./ColumnPicker";

const FORMAT_TONE = { ctv: "blue", hbbtv: "green", broadcast: "yellow" };

function Chips({ values, tone }) {
  if (!values?.length) return <span className="muted">—</span>;
  return (
    <div className="cell-chips">
      {values.map((v) => (
        <span key={v} className={`chip chip-${tone ? tone(v) : "gray"}`}>
          {v}
        </span>
      ))}
    </div>
  );
}

// How each column draws its cell
function renderCell(field, row, onOpen) {
  switch (field.key) {
    case "channelName":
      return (
        <button type="button" className="cell-open" onClick={() => onOpen(row)} title="Show details">
          <span className="cell-strong">{row.channelName}</span>
        </button>
      );
    case "formats":
      return <Chips values={row.formats} tone={(v) => FORMAT_TONE[v.toLowerCase()] || "gray"} />;
    case "genres":
    case "adFormats":
    case "adTypes":
    case "territories":
      return <Chips values={row[field.key]} />;
    case "cpm":
      return row.cpm == null ? <span className="muted">—</span> : formatMoney(row.cpm, row.currency);
    case "reach":
      return row.reach == null ? <span className="muted">—</span> : formatReach(row.reach);
    case "contact":
      if (!row.contactName && !row.contactEmail) return <span className="muted">—</span>;
      return (
        <div className="cell-contact">
          <span>{row.contactName || row.contactEmail}</span>
          {row.contactEmail && (
            <a href={`mailto:${row.contactEmail}`} title={row.contactEmail}>
              {row.contactName ? row.contactEmail : <Icon name="mail" size={14} />}
            </a>
          )}
        </div>
      );
    case "updatedAt":
      if (!row.updatedAt) return <span className="muted">—</span>;
      return (
        <div className="cell-updated" title={formatDateTime(row.updatedAt)}>
          <span>{timeAgo(row.updatedAt)}</span>
          {row.updatedBy && <small>{row.updatedBy}</small>}
        </div>
      );
    default:
      return row[field.key] || <span className="muted">—</span>;
  }
}

export default function InventoryTable({
  rows,
  columns,
  columnPrefs,
  sort,
  onSort,
  selected,
  onToggle,
  onToggleAll,
  onEdit,
  onDuplicate,
  onDelete,
  onOpen,
  canEdit,
}) {
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someOnPage = rows.some((r) => selected.has(r.id));

  return (
    <div className="table-wrap">
      <table className="inv-table">
        <thead>
          <tr>
            <th className="col-check">
              <input
                type="checkbox"
                aria-label="Select all on this page"
                checked={allOnPage}
                ref={(el) => el && (el.indeterminate = someOnPage && !allOnPage)}
                onChange={() => onToggleAll(rows, !allOnPage)}
              />
            </th>
            {columns.map((field) => {
              const active = sort.key === field.key;
              return (
                <th
                  key={field.key}
                  className={`col-${field.key}${field.numeric ? " num" : ""}`}
                  aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                >
                  <span className="th-inner">
                    <button type="button" className="th-sort" onClick={() => onSort(field.key)}>
                      {field.label}
                      <Icon
                        name={active ? (sort.dir === "asc" ? "up" : "down") : "sort"}
                        size={13}
                        className={active ? "" : "sort-idle"}
                      />
                    </button>
                    {field.key !== columnPrefs.pinned && (
                      <button
                        type="button"
                        className="th-hide"
                        title={`Hide ${field.label}`}
                        aria-label={`Hide ${field.label} column`}
                        onClick={() => columnPrefs.setVisible(field.key, false)}
                      >
                        <Icon name="x" size={12} />
                      </button>
                    )}
                  </span>
                </th>
              );
            })}
            <th className="col-actions">
              <div className="th-add">
                <ColumnPicker
                  columns={columnPrefs}
                  triggerClass="icon-btn"
                  label="Add or remove columns"
                  trigger={<Icon name="plus" />}
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={selected.has(row.id) ? "is-selected" : ""}>
              <td className="col-check">
                <input
                  type="checkbox"
                  aria-label={`Select ${row.channelName}`}
                  checked={selected.has(row.id)}
                  onChange={() => onToggle(row.id)}
                />
              </td>
              {columns.map((field) => (
                <td key={field.key} className={`col-${field.key}${field.numeric ? " num" : ""}`}>
                  {renderCell(field, row, onOpen)}
                </td>
              ))}
              <td className="col-actions">
                {canEdit && (
                  <div className="row-actions">
                    <button type="button" className="icon-btn" title="Edit" aria-label={`Edit ${row.channelName}`} onClick={() => onEdit(row)}>
                      <Icon name="edit" />
                    </button>
                    <button type="button" className="icon-btn" title="Duplicate" aria-label={`Duplicate ${row.channelName}`} onClick={() => onDuplicate(row)}>
                      <Icon name="copy" />
                    </button>
                    <button type="button" className="icon-btn danger" title="Delete" aria-label={`Delete ${row.channelName}`} onClick={() => onDelete([row])}>
                      <Icon name="trash" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
